import { useState, useRef } from 'react';
import { router, Link } from '@inertiajs/react';
import GmLayout from '@/layouts/GmLayout';
import { Editor } from '@/components/Editor';
import { ImageCropper } from '@/components/ImageCropper';

/**
 * Newspaper article data from the backend.
 */
interface NewspaperArticle {
    id: number;
    issue_id: number | null;
    issue_number: number;
    title: string;
    slug: string;
    byline: string | null;
    content: string;
    excerpt: string | null;
    cover_image_url: string | null;
    is_published: boolean;
    published_at: string | null;
    created_at: string;
    updated_at: string;
}

/**
 * Issue data for selection.
 */
interface Issue {
    id: number;
    issue_number: number;
    title: string | null;
    display_title: string;
    article_count: number;
    is_published: boolean;
}

/**
 * Props for the Edit page.
 */
interface Props {
    article: NewspaperArticle;
    issues: Issue[];
    nextIssueNumber: number;
}

/**
 * GM Newspaper Edit page - form to edit an existing newspaper article.
 */
export default function Edit({ article, issues, nextIssueNumber }: Props) {
    // Form fields - pre-populated from article
    const [title, setTitle] = useState(article.title);
    const [byline, setByline] = useState(article.byline || '');
    const [content, setContent] = useState(article.content);
    const [excerpt, setExcerpt] = useState(article.excerpt || '');
    const [coverImage, setCoverImage] = useState<File | null>(null);
    const [coverImagePreview, setCoverImagePreview] = useState<string | null>(null);
    const [removeCoverImage, setRemoveCoverImage] = useState(false);
    const [isPublished, setIsPublished] = useState(article.is_published);

    // Issue selection state
    const [issueMode, setIssueMode] = useState<'existing' | 'new'>(article.issue_id ? 'existing' : 'new');
    const [selectedIssueId, setSelectedIssueId] = useState<number | null>(article.issue_id);
    const [newIssueTitle, setNewIssueTitle] = useState('');

    // Image cropper state
    const [imageToCrop, setImageToCrop] = useState<string | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);

    // Form state
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [errors, setErrors] = useState<Record<string, string>>({});

    /**
     * Handle file selection - opens the cropper modal.
     */
    const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        // Read the file and open the cropper
        const reader = new FileReader();
        reader.onload = () => {
            setImageToCrop(reader.result as string);
        };
        reader.readAsDataURL(file);
    };

    /**
     * Handle crop completion - receives the cropped image blob.
     */
    const handleCropComplete = (croppedBlob: Blob) => {
        // Convert blob to File for FormData
        const croppedFile = new File([croppedBlob], 'cover.jpg', { type: 'image/jpeg' });
        setCoverImage(croppedFile);

        // Create preview URL
        setCoverImagePreview(URL.createObjectURL(croppedBlob));

        // Close cropper and clear remove flag
        setImageToCrop(null);
        setRemoveCoverImage(false);

        // Reset file input
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    /**
     * Handle crop cancellation.
     */
    const handleCropCancel = () => {
        setImageToCrop(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    /**
     * Handle form submission.
     */
    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        setIsSubmitting(true);
        setErrors({});

        // Use FormData for file upload
        const formData = new FormData();
        formData.append('_method', 'PATCH'); // Laravel method spoofing
        formData.append('title', title);
        formData.append('content', content);
        formData.append('is_published', isPublished ? '1' : '0');
        formData.append('remove_cover_image', removeCoverImage ? '1' : '0');
        if (byline) formData.append('byline', byline);
        if (excerpt) formData.append('excerpt', excerpt);
        if (coverImage) formData.append('cover_image', coverImage);

        // Issue selection
        if (issueMode === 'new') {
            formData.append('create_new_issue', '1');
            if (newIssueTitle) formData.append('new_issue_title', newIssueTitle);
        } else if (selectedIssueId) {
            formData.append('issue_id', selectedIssueId.toString());
        }

        router.post(`/gm/newspaper/${article.id}`, formData, {
            forceFormData: true,
            onError: (errs) => {
                setErrors(errs);
                setIsSubmitting(false);
            },
            onSuccess: () => setIsSubmitting(false),
        });
    };

    // Get current issue display
    const currentIssue = issues.find(i => i.id === article.issue_id);

    return (
        <GmLayout title={`Edit: ${article.title}`} activePath="/gm/newspaper">
            <div className="max-w-4xl">
                <div className="flex items-center justify-between mb-6">
                    <div>
                        <h1 className="text-2xl font-bold text-white">Edit Article</h1>
                        <p className="text-sm text-gray-400">
                            {currentIssue ? currentIssue.display_title : 'No issue assigned'}
                        </p>
                    </div>
                    <Link href="/gm/newspaper" className="text-gray-400 hover:text-white transition-colors">
                        Back to Newspaper
                    </Link>
                </div>

                <form onSubmit={handleSubmit} className="space-y-6">
                    {/* Issue Selection Section */}
                    <div className="bg-slate-800 border border-slate-700 rounded-lg p-6 space-y-4">
                        <h2 className="text-lg font-semibold text-white mb-4">Issue Assignment</h2>

                        <div className="flex gap-4 mb-4">
                            <label className="flex items-center gap-2 text-sm text-gray-300 cursor-pointer">
                                <input
                                    type="radio"
                                    name="issueMode"
                                    checked={issueMode === 'existing'}
                                    onChange={() => setIssueMode('existing')}
                                    className="text-purple-600"
                                />
                                Use existing issue
                            </label>
                            <label className="flex items-center gap-2 text-sm text-gray-300 cursor-pointer">
                                <input
                                    type="radio"
                                    name="issueMode"
                                    checked={issueMode === 'new'}
                                    onChange={() => setIssueMode('new')}
                                    className="text-purple-600"
                                />
                                Move to new issue
                            </label>
                        </div>

                        {issueMode === 'existing' ? (
                            <div>
                                <label className="block text-sm font-medium text-gray-300 mb-1">
                                    Select Issue
                                </label>
                                <select
                                    value={selectedIssueId ?? ''}
                                    onChange={(e) => setSelectedIssueId(e.target.value ? Number(e.target.value) : null)}
                                    className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded text-white"
                                >
                                    <option value="">Select an issue...</option>
                                    {issues.map((issue) => (
                                        <option key={issue.id} value={issue.id}>
                                            {issue.display_title} ({issue.article_count} article{issue.article_count !== 1 ? 's' : ''})
                                            {issue.is_published ? ' - Published' : ' - Draft'}
                                        </option>
                                    ))}
                                </select>
                                {errors.issue_id && <p className="mt-1 text-sm text-red-400">{errors.issue_id}</p>}
                            </div>
                        ) : (
                            <div>
                                <label className="block text-sm font-medium text-gray-300 mb-1">
                                    New Issue Title (optional)
                                </label>
                                <input
                                    type="text"
                                    value={newIssueTitle}
                                    onChange={(e) => setNewIssueTitle(e.target.value)}
                                    className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded text-white"
                                    placeholder={`Leave blank for "Issue #${nextIssueNumber}"`}
                                />
                                <p className="text-xs text-gray-500 mt-1">
                                    This will create Issue #{nextIssueNumber} and move this article to it
                                </p>
                            </div>
                        )}
                    </div>

                    {/* Basic Info Section */}
                    <div className="bg-slate-800 border border-slate-700 rounded-lg p-6 space-y-4">
                        <h2 className="text-lg font-semibold text-white mb-4">Article Information</h2>

                        {/* Title */}
                        <div>
                            <label className="block text-sm font-medium text-gray-300 mb-1">
                                Headline <span className="text-red-400">*</span>
                            </label>
                            <input
                                type="text"
                                value={title}
                                onChange={(e) => setTitle(e.target.value)}
                                className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded text-white"
                                required
                            />
                            {errors.title && <p className="mt-1 text-sm text-red-400">{errors.title}</p>}
                        </div>

                        {/* Slug (read-only) */}
                        <div>
                            <label className="block text-sm font-medium text-gray-300 mb-1">URL Slug</label>
                            <div className="px-3 py-2 bg-slate-900 border border-slate-700 rounded text-gray-400 text-sm">
                                /newspaper/{article.slug}
                            </div>
                        </div>

                        {/* Byline */}
                        <div>
                            <label className="block text-sm font-medium text-gray-300 mb-1">
                                Byline (Author Name)
                            </label>
                            <input
                                type="text"
                                value={byline}
                                onChange={(e) => setByline(e.target.value)}
                                className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded text-white"
                                placeholder="Leave blank to use your name"
                            />
                            {errors.byline && <p className="mt-1 text-sm text-red-400">{errors.byline}</p>}
                        </div>

                        {/* Cover Image */}
                        <div>
                            <label className="block text-sm font-medium text-gray-300 mb-1">
                                Cover Image
                            </label>
                            <p className="text-xs text-gray-500 mb-2">
                                Image will be cropped to 864×384 pixels (2.25:1 aspect ratio)
                            </p>
                            {/* Preview of new cropped image */}
                            {coverImagePreview && (
                                <div className="mb-3">
                                    <p className="text-xs text-green-400 mb-1">New cover image:</p>
                                    <img
                                        src={coverImagePreview}
                                        alt="New cover preview"
                                        className="w-full max-w-md h-auto rounded border border-green-600"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setCoverImage(null);
                                            setCoverImagePreview(null);
                                            if (fileInputRef.current) {
                                                fileInputRef.current.value = '';
                                            }
                                        }}
                                        className="mt-2 text-xs text-red-400 hover:text-red-300"
                                    >
                                        Remove new image
                                    </button>
                                </div>
                            )}
                            {/* Existing cover image (only show if no new image and not marked for removal) */}
                            {article.cover_image_url && !removeCoverImage && !coverImagePreview && (
                                <div className="mb-3">
                                    <p className="text-xs text-gray-400 mb-1">Current cover image:</p>
                                    <img
                                        src={article.cover_image_url}
                                        alt="Current cover"
                                        className="w-full max-w-md h-auto rounded border border-slate-600"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setRemoveCoverImage(true)}
                                        className="mt-2 text-xs text-red-400 hover:text-red-300"
                                    >
                                        Remove cover image
                                    </button>
                                </div>
                            )}
                            {removeCoverImage && !coverImagePreview && (
                                <p className="mb-3 text-sm text-yellow-400">
                                    Cover image will be removed on save.{' '}
                                    <button
                                        type="button"
                                        onClick={() => setRemoveCoverImage(false)}
                                        className="underline"
                                    >
                                        Undo
                                    </button>
                                </p>
                            )}
                            <input
                                ref={fileInputRef}
                                type="file"
                                accept="image/*"
                                onChange={handleFileSelect}
                                className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded text-white file:mr-4 file:py-1 file:px-4 file:rounded file:border-0 file:bg-purple-600 file:text-white file:cursor-pointer"
                            />
                            {errors.cover_image && <p className="mt-1 text-sm text-red-400">{errors.cover_image}</p>}
                        </div>

                        {/* Content - Markdown Editor */}
                        <div>
                            <label className="block text-sm font-medium text-gray-300 mb-1">
                                Article Content <span className="text-red-400">*</span>
                            </label>
                            <div data-color-mode="dark">
                                <Editor
                                    value={content}
                                    onChange={(value) => setContent(value)}
                                    placeholder="Write your article here..."
                                    height={400}
                                />
                            </div>
                            {errors.content && <p className="mt-1 text-sm text-red-400">{errors.content}</p>}
                        </div>

                        {/* Excerpt */}
                        <div>
                            <label className="block text-sm font-medium text-gray-300 mb-1">
                                Excerpt (Preview Text)
                            </label>
                            <textarea
                                value={excerpt}
                                onChange={(e) => setExcerpt(e.target.value)}
                                className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded text-white"
                                placeholder="Leave blank to auto-generate from first paragraph"
                                rows={3}
                            />
                            {errors.excerpt && <p className="mt-1 text-sm text-red-400">{errors.excerpt}</p>}
                        </div>

                        {/* Published Status */}
                        <div className="flex items-center">
                            <label className="flex items-center gap-2 text-sm text-gray-300">
                                <input
                                    type="checkbox"
                                    checked={isPublished}
                                    onChange={(e) => setIsPublished(e.target.checked)}
                                    className="rounded bg-slate-700 border-slate-600 text-purple-600"
                                />
                                Published (visible to all users)
                            </label>
                        </div>
                        {article.published_at && (
                            <p className="text-xs text-gray-500">
                                First published: {new Date(article.published_at).toLocaleDateString()}
                            </p>
                        )}
                    </div>

                    {/* Submit Buttons */}
                    <div className="flex gap-4">
                        <button
                            type="submit"
                            disabled={isSubmitting}
                            className="px-6 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded transition-colors disabled:opacity-50"
                        >
                            {isSubmitting ? 'Saving...' : 'Save Changes'}
                        </button>
                        <Link
                            href="/gm/newspaper"
                            className="px-6 py-2 bg-slate-600 hover:bg-slate-500 text-white rounded transition-colors"
                        >
                            Close
                        </Link>
                        {article.is_published && (
                            <a
                                href={`/newspaper/${article.slug}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="px-6 py-2 bg-green-600 hover:bg-green-700 text-white rounded transition-colors"
                            >
                                View Article
                            </a>
                        )}
                    </div>
                </form>
            </div>

            {/* Image Cropper Modal */}
            {imageToCrop && (
                <ImageCropper
                    imageSrc={imageToCrop}
                    onCropComplete={handleCropComplete}
                    onCancel={handleCropCancel}
                    targetWidth={864}
                    targetHeight={384}
                />
            )}
        </GmLayout>
    );
}

